import { lightningColorRange } from "@/geojson/definitions";
import type { LightningBinaryChunk, LightningBinaryHeader, LightningBinaryPayload } from "./LightningBinaryPayload";
import {
  LIGHTNING_PICK_MAX_PASSES,
  LIGHTNING_PICK_MAX_ROWS,
  LIGHTNING_PICK_RADIUS_CSS_PX,
  LIGHTNING_PICK_SIZE_CSS_PX,
  type LightningBinaryPickResult,
  collectLightningPickPass,
  finalizeLightningPickHits,
} from "./LightningBinaryPicking";
import { createLightningIconAtlas } from "./gl/iconAtlas";
import { createLightningPickingProgram, createLightningProgram } from "./gl/programs";

type Uniforms = {
  matrix: WebGLUniformLocation;
  viewportSize: WebGLUniformLocation;
  devicePixelRatio: WebGLUniformLocation;
  iconSizeCssPx: WebGLUniformLocation;
  worldOffset: WebGLUniformLocation;
  wireVersion: WebGLUniformLocation;
  chunkStartEpoch: WebGLUniformLocation;
  windowStartEpoch: WebGLUniformLocation;
  selectedEpoch: WebGLUniformLocation;
  typeMask: WebGLUniformLocation;
  ageColors: WebGLUniformLocation;
  iconAtlas: WebGLUniformLocation;
  opacity: WebGLUniformLocation;
};

type PickingUniforms = {
  matrix: WebGLUniformLocation;
  viewportSizeCss: WebGLUniformLocation;
  queryOriginCss: WebGLUniformLocation;
  querySizeCss: WebGLUniformLocation;
  worldOffset: WebGLUniformLocation;
  wireVersion: WebGLUniformLocation;
  chunkStartEpoch: WebGLUniformLocation;
  windowStartEpoch: WebGLUniformLocation;
  selectedEpoch: WebGLUniformLocation;
  typeMask: WebGLUniformLocation;
  pointBase: WebGLUniformLocation;
  cutoffTexture: WebGLUniformLocation;
  useCutoff: WebGLUniformLocation;
};

type PickingResources = {
  program: WebGLProgram;
  framebuffer: WebGLFramebuffer;
  resultTexture: WebGLTexture;
  cutoffTexture: WebGLTexture;
  uniforms: PickingUniforms;
  width: number;
  height: number;
  readback: Uint8Array;
};

type PickingGlState = {
  drawFramebuffer: WebGLFramebuffer | null;
  readFramebuffer: WebGLFramebuffer | null;
  viewport: Int32Array;
  program: WebGLProgram | null;
  vao: WebGLVertexArrayObject | null;
  arrayBuffer: WebGLBuffer | null;
  pixelPackBuffer: WebGLBuffer | null;
  pixelUnpackBuffer: WebGLBuffer | null;
  activeTexture: number;
  activeTextureBinding: WebGLTexture | null;
  pickingTextureBinding: WebGLTexture | null;
  colorMask: readonly [boolean, boolean, boolean, boolean];
  depthMask: boolean;
  blend: boolean;
  depthTest: boolean;
  dither: boolean;
  scissorTest: boolean;
  rasterizerDiscard: boolean;
};

export type LightningBinaryRenderOptions = {
  matrix: readonly number[];
  viewportWidth: number;
  viewportHeight: number;
  devicePixelRatio: number;
  iconSizeCssPx: number;
  opacity: number;
  selectedTimeMs: number;
  durationMs: number;
  typeMask: number;
  worldOffsets: readonly number[];
};

export type LightningBinaryPickOptions = LightningBinaryRenderOptions & {
  clickXCss: number;
  clickYCss: number;
};

function requiredUniform(gl: WebGL2RenderingContext, program: WebGLProgram, name: string): WebGLUniformLocation {
  const location = gl.getUniformLocation(program, name);
  if (!location) {
    throw new Error(`Lightning shader uniform was removed or not linked: ${name}`);
  }
  return location;
}

function colorComponents(color: string): [number, number, number, number] {
  const hex = color.startsWith("#") ? color.slice(1) : color;
  if (hex.length !== 6) {
    throw new Error(`Unsupported lightning color ${color}`);
  }
  return [
    Number.parseInt(hex.slice(0, 2), 16) / 255,
    Number.parseInt(hex.slice(2, 4), 16) / 255,
    Number.parseInt(hex.slice(4, 6), 16) / 255,
    1,
  ];
}

const AGE_COLOR_COMPONENTS = new Float32Array(lightningColorRange.flatMap(colorComponents));
const EMPTY_PICK_COLOR = new Uint32Array([0, 0, 0, 0]);
const MAX_PICKABLE_POINT_COUNT = 0xffff_ffff;

export function lightningWorldOffsets(centerLongitude: number): readonly number[] {
  const centerWorld = Math.floor((centerLongitude + 180) / 360);
  return [centerWorld - 1, centerWorld, centerWorld + 1];
}

export class LightningBinaryRenderer {
  private gl?: WebGL2RenderingContext;
  private program?: WebGLProgram;
  private vao?: WebGLVertexArrayObject;
  private responseBuffer?: WebGLBuffer;
  private iconTexture?: WebGLTexture;
  private uniforms?: Uniforms;
  private payload?: LightningBinaryPayload;
  private pendingPayload?: LightningBinaryPayload;
  private picking?: PickingResources;
  private visible = false;

  onAdd(gl: WebGL2RenderingContext): void {
    this.dispose();
    const program = createLightningProgram(gl);
    const vao = gl.createVertexArray();
    const responseBuffer = gl.createBuffer();
    const iconTexture = gl.createTexture();
    if (!vao || !responseBuffer || !iconTexture) {
      gl.deleteProgram(program);
      if (vao) {
        gl.deleteVertexArray(vao);
      }
      if (responseBuffer) {
        gl.deleteBuffer(responseBuffer);
      }
      if (iconTexture) {
        gl.deleteTexture(iconTexture);
      }
      throw new Error("Unable to allocate lightning WebGL resources");
    }

    this.gl = gl;
    this.program = program;
    this.vao = vao;
    this.responseBuffer = responseBuffer;
    this.iconTexture = iconTexture;
    this.uniforms = {
      matrix: requiredUniform(gl, program, "u_matrix"),
      viewportSize: requiredUniform(gl, program, "u_viewportSize"),
      devicePixelRatio: requiredUniform(gl, program, "u_devicePixelRatio"),
      iconSizeCssPx: requiredUniform(gl, program, "u_iconSizeCssPx"),
      worldOffset: requiredUniform(gl, program, "u_worldOffset"),
      wireVersion: requiredUniform(gl, program, "u_wireVersion"),
      chunkStartEpoch: requiredUniform(gl, program, "u_chunkStartEpoch"),
      windowStartEpoch: requiredUniform(gl, program, "u_windowStartEpoch"),
      selectedEpoch: requiredUniform(gl, program, "u_selectedEpoch"),
      typeMask: requiredUniform(gl, program, "u_typeMask"),
      ageColors: requiredUniform(gl, program, "u_ageColors[0]"),
      iconAtlas: requiredUniform(gl, program, "u_iconAtlas"),
      opacity: requiredUniform(gl, program, "u_opacity"),
    };

    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, responseBuffer);
    for (let location = 0; location < 5; location++) {
      gl.enableVertexAttribArray(location);
      gl.vertexAttribDivisor(location, 1);
    }
    gl.bindVertexArray(null);

    const atlas = createLightningIconAtlas();
    gl.bindTexture(gl.TEXTURE_2D, iconTexture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, atlas.width, atlas.height, 0, gl.RED, gl.UNSIGNED_BYTE, atlas.pixels);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, null);

    const payload = this.pendingPayload ?? this.payload;
    this.payload = undefined;
    if (payload) {
      this.uploadPayload(payload);
    }
  }

  setPayload(payload: LightningBinaryPayload): void {
    this.pendingPayload = payload;
    if (!this.gl || !this.responseBuffer) {
      return;
    }
    if (this.payload?.buffer === payload.buffer) {
      return;
    }
    this.uploadPayload(payload);
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
  }

  private uploadPayload(payload: LightningBinaryPayload): void {
    const gl = this.gl;
    if (!gl || !this.responseBuffer) {
      return;
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, this.responseBuffer);
    // The complete response, including header and descriptors, is uploaded once.
    gl.bufferData(gl.ARRAY_BUFFER, payload.buffer, gl.STATIC_DRAW);
    this.payload = payload;
    this.pendingPayload = payload;
  }

  private configureVersionAttributes(header: LightningBinaryHeader): void {
    const gl = this.gl;
    if (!gl) {
      return;
    }
    if (header.wireVersion === 2) {
      gl.disableVertexAttribArray(2);
      gl.vertexAttrib1f(2, 0);
      gl.disableVertexAttribArray(3);
      gl.vertexAttribI4ui(3, 0, 0, 0, 0);
      gl.enableVertexAttribArray(4);
    } else {
      gl.enableVertexAttribArray(2);
      gl.enableVertexAttribArray(3);
      gl.enableVertexAttribArray(4);
    }
  }

  private bindChunkAttributes(header: LightningBinaryHeader, chunk: LightningBinaryChunk): void {
    const gl = this.gl;
    if (!gl) {
      return;
    }
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 8, header.positionsOffset + chunk.pointOffset * 8);
    if (header.wireVersion === 2) {
      gl.vertexAttribIPointer(1, 1, gl.UNSIGNED_SHORT, 2, header.timesOffset + chunk.pointOffset * 2);
      gl.vertexAttribIPointer(4, 1, gl.UNSIGNED_BYTE, 1, header.polaritiesOffset + chunk.pointOffset);
    } else {
      gl.vertexAttribIPointer(1, 1, gl.UNSIGNED_SHORT, 2, header.timesOffset + chunk.pointOffset * 2);
      gl.vertexAttribPointer(2, 1, gl.FLOAT, false, 4, header.currentsOffset + chunk.pointOffset * 4);
      gl.vertexAttribIPointer(3, 1, gl.UNSIGNED_BYTE, 1, header.typesOffset + chunk.pointOffset);
      gl.vertexAttribIPointer(4, 1, gl.UNSIGNED_BYTE, 1, header.polaritiesOffset + chunk.pointOffset);
    }
  }

  render(options: LightningBinaryRenderOptions): void {
    const { gl, program, vao, responseBuffer, iconTexture, uniforms, payload } = this;
    if (!this.visible || !gl || !program || !vao || !responseBuffer || !iconTexture || !uniforms || !payload) {
      return;
    }

    gl.useProgram(program);
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, responseBuffer);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, iconTexture);
    gl.uniform1i(uniforms.iconAtlas, 0);
    gl.uniformMatrix4fv(uniforms.matrix, false, options.matrix);
    gl.uniform2f(uniforms.viewportSize, options.viewportWidth, options.viewportHeight);
    gl.uniform1f(uniforms.devicePixelRatio, options.devicePixelRatio);
    gl.uniform1f(uniforms.iconSizeCssPx, options.iconSizeCssPx);
    gl.uniform1f(uniforms.opacity, Math.max(0, Math.min(1, options.opacity)));
    gl.uniform1ui(uniforms.typeMask, options.typeMask);
    gl.uniform4fv(uniforms.ageColors, AGE_COLOR_COMPONENTS);

    const selectedEpoch = Math.ceil(options.selectedTimeMs / 1000);
    const windowStartEpoch = Math.ceil((options.selectedTimeMs - options.durationMs) / 1000);
    gl.uniform1ui(uniforms.selectedEpoch, selectedEpoch);
    gl.uniform1ui(uniforms.windowStartEpoch, windowStartEpoch);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.disable(gl.DEPTH_TEST);
    gl.depthMask(false);

    const header = payload.header;
    gl.uniform1ui(uniforms.wireVersion, header.wireVersion);
    this.configureVersionAttributes(header);

    for (const chunk of header.chunks) {
      if (chunk.pointCount === 0) {
        continue;
      }
      this.bindChunkAttributes(header, chunk);
      gl.uniform1ui(uniforms.chunkStartEpoch, chunk.startEpoch);
      for (const worldOffset of options.worldOffsets) {
        gl.uniform1f(uniforms.worldOffset, worldOffset);
        gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, chunk.pointCount);
      }
    }

    gl.depthMask(true);
    gl.bindVertexArray(null);
  }

  pick(options: LightningBinaryPickOptions): LightningBinaryPickResult {
    const { gl, vao, responseBuffer, payload } = this;
    if (!this.visible || !gl || !vao || !responseBuffer || !payload || payload.header.totalPointCount === 0) {
      return { hits: [], truncated: false };
    }
    if (payload.header.totalPointCount > MAX_PICKABLE_POINT_COUNT) {
      throw new Error("Lightning payload contains more points than the picking ID format supports");
    }

    const devicePixelRatio = Math.max(1, options.devicePixelRatio);
    const targetSize = Math.max(1, Math.round(LIGHTNING_PICK_SIZE_CSS_PX * devicePixelRatio));
    const state = this.capturePickingState(gl);
    try {
      const picking = this.ensurePickingResources(gl, targetSize, targetSize);
      const { uniforms } = picking;
      gl.bindFramebuffer(gl.FRAMEBUFFER, picking.framebuffer);
      gl.viewport(0, 0, picking.width, picking.height);
      gl.useProgram(picking.program);
      gl.bindVertexArray(vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, responseBuffer);
      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
      gl.bindBuffer(gl.PIXEL_UNPACK_BUFFER, null);
      gl.uniformMatrix4fv(uniforms.matrix, false, options.matrix);
      gl.uniform2f(
        uniforms.viewportSizeCss,
        options.viewportWidth / devicePixelRatio,
        options.viewportHeight / devicePixelRatio,
      );
      gl.uniform2f(
        uniforms.queryOriginCss,
        options.clickXCss - LIGHTNING_PICK_RADIUS_CSS_PX,
        options.clickYCss - LIGHTNING_PICK_RADIUS_CSS_PX,
      );
      gl.uniform1f(uniforms.querySizeCss, LIGHTNING_PICK_SIZE_CSS_PX);
      gl.uniform1ui(uniforms.wireVersion, payload.header.wireVersion);
      gl.uniform1ui(uniforms.typeMask, options.typeMask);
      gl.uniform1ui(uniforms.selectedEpoch, Math.ceil(options.selectedTimeMs / 1000));
      gl.uniform1ui(uniforms.windowStartEpoch, Math.ceil((options.selectedTimeMs - options.durationMs) / 1000));
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, picking.cutoffTexture);
      gl.uniform1i(uniforms.cutoffTexture, 1);

      gl.disable(gl.BLEND);
      gl.disable(gl.DEPTH_TEST);
      gl.disable(gl.DITHER);
      gl.disable(gl.SCISSOR_TEST);
      gl.disable(gl.RASTERIZER_DISCARD);
      gl.colorMask(true, true, true, true);
      gl.depthMask(false);
      this.configureVersionAttributes(payload.header);

      const hits = new Map<number, number>();
      let truncated = false;
      let totalPasses = 0;
      const centerWorldOffset = options.worldOffsets[Math.floor(options.worldOffsets.length / 2)] ?? 0;
      const pickWorldOffsets = [...options.worldOffsets].sort(
        (left, right) => Math.abs(left - centerWorldOffset) - Math.abs(right - centerWorldOffset),
      );
      for (const worldOffset of pickWorldOffsets) {
        for (let worldPass = 0; totalPasses < LIGHTNING_PICK_MAX_PASSES; worldPass++) {
          totalPasses += 1;
          if (worldPass > 0) {
            gl.texSubImage2D(
              gl.TEXTURE_2D,
              0,
              0,
              0,
              picking.width,
              picking.height,
              gl.RGBA_INTEGER,
              gl.UNSIGNED_BYTE,
              picking.readback,
            );
          }
          gl.uniform1ui(uniforms.useCutoff, worldPass === 0 ? 0 : 1);
          gl.clearBufferuiv(gl.COLOR, 0, EMPTY_PICK_COLOR);
          gl.uniform1f(uniforms.worldOffset, worldOffset);
          for (const chunk of payload.header.chunks) {
            if (chunk.pointCount === 0) {
              continue;
            }
            this.bindChunkAttributes(payload.header, chunk);
            gl.uniform1ui(uniforms.chunkStartEpoch, chunk.startEpoch);
            gl.uniform1ui(uniforms.pointBase, chunk.pointOffset);
            gl.drawArraysInstanced(gl.POINTS, 0, 1, chunk.pointCount);
          }
          gl.readPixels(0, 0, picking.width, picking.height, gl.RGBA_INTEGER, gl.UNSIGNED_BYTE, picking.readback);
          const found = collectLightningPickPass(
            picking.readback,
            picking.width,
            picking.height,
            devicePixelRatio,
            hits,
          );
          if (!found) {
            break;
          }
          if (hits.size >= LIGHTNING_PICK_MAX_ROWS) {
            truncated = true;
            break;
          }
          if (totalPasses === LIGHTNING_PICK_MAX_PASSES) {
            truncated = true;
          }
        }
        if (truncated || totalPasses >= LIGHTNING_PICK_MAX_PASSES) {
          break;
        }
      }

      const result = finalizeLightningPickHits(hits);
      return { hits: result.hits, truncated: truncated || result.truncated };
    } finally {
      this.restorePickingState(gl, state);
    }
  }

  private ensurePickingResources(gl: WebGL2RenderingContext, width: number, height: number): PickingResources {
    let picking = this.picking;
    if (!picking) {
      const program = createLightningPickingProgram(gl);
      const framebuffer = gl.createFramebuffer();
      const resultTexture = gl.createTexture();
      const cutoffTexture = gl.createTexture();
      if (!framebuffer || !resultTexture || !cutoffTexture) {
        gl.deleteProgram(program);
        if (framebuffer) {
          gl.deleteFramebuffer(framebuffer);
        }
        if (resultTexture) {
          gl.deleteTexture(resultTexture);
        }
        if (cutoffTexture) {
          gl.deleteTexture(cutoffTexture);
        }
        throw new Error("Unable to allocate lightning picking resources");
      }
      try {
        picking = {
          program,
          framebuffer,
          resultTexture,
          cutoffTexture,
          uniforms: {
            matrix: requiredUniform(gl, program, "u_matrix"),
            viewportSizeCss: requiredUniform(gl, program, "u_viewportSizeCss"),
            queryOriginCss: requiredUniform(gl, program, "u_queryOriginCss"),
            querySizeCss: requiredUniform(gl, program, "u_querySizeCss"),
            worldOffset: requiredUniform(gl, program, "u_worldOffset"),
            wireVersion: requiredUniform(gl, program, "u_wireVersion"),
            chunkStartEpoch: requiredUniform(gl, program, "u_chunkStartEpoch"),
            windowStartEpoch: requiredUniform(gl, program, "u_windowStartEpoch"),
            selectedEpoch: requiredUniform(gl, program, "u_selectedEpoch"),
            typeMask: requiredUniform(gl, program, "u_typeMask"),
            pointBase: requiredUniform(gl, program, "u_pointBase"),
            cutoffTexture: requiredUniform(gl, program, "u_cutoffTexture"),
            useCutoff: requiredUniform(gl, program, "u_useCutoff"),
          },
          width: 0,
          height: 0,
          readback: new Uint8Array(0),
        };
      } catch (error) {
        gl.deleteProgram(program);
        gl.deleteFramebuffer(framebuffer);
        gl.deleteTexture(resultTexture);
        gl.deleteTexture(cutoffTexture);
        throw error;
      }
      this.picking = picking;
    }

    if (picking.width === width && picking.height === height) {
      return picking;
    }

    gl.activeTexture(gl.TEXTURE1);
    for (const texture of [picking.resultTexture, picking.cutoffTexture]) {
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8UI, width, height, 0, gl.RGBA_INTEGER, gl.UNSIGNED_BYTE, null);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, picking.framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, picking.resultTexture, 0);
    if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
      this.disposePickingResources();
      throw new Error("Lightning picking framebuffer is incomplete");
    }
    picking.width = width;
    picking.height = height;
    picking.readback = new Uint8Array(width * height * 4);
    return picking;
  }

  private capturePickingState(gl: WebGL2RenderingContext): PickingGlState {
    const activeTexture = gl.getParameter(gl.ACTIVE_TEXTURE) as number;
    const activeTextureBinding = gl.getParameter(gl.TEXTURE_BINDING_2D) as WebGLTexture | null;
    gl.activeTexture(gl.TEXTURE1);
    const pickingTextureBinding = gl.getParameter(gl.TEXTURE_BINDING_2D) as WebGLTexture | null;
    gl.activeTexture(activeTexture);
    const colorMaskValue = gl.getParameter(gl.COLOR_WRITEMASK) as boolean[];
    return {
      drawFramebuffer: gl.getParameter(gl.DRAW_FRAMEBUFFER_BINDING) as WebGLFramebuffer | null,
      readFramebuffer: gl.getParameter(gl.READ_FRAMEBUFFER_BINDING) as WebGLFramebuffer | null,
      viewport: new Int32Array(gl.getParameter(gl.VIEWPORT) as Int32Array),
      program: gl.getParameter(gl.CURRENT_PROGRAM) as WebGLProgram | null,
      vao: gl.getParameter(gl.VERTEX_ARRAY_BINDING) as WebGLVertexArrayObject | null,
      arrayBuffer: gl.getParameter(gl.ARRAY_BUFFER_BINDING) as WebGLBuffer | null,
      pixelPackBuffer: gl.getParameter(gl.PIXEL_PACK_BUFFER_BINDING) as WebGLBuffer | null,
      pixelUnpackBuffer: gl.getParameter(gl.PIXEL_UNPACK_BUFFER_BINDING) as WebGLBuffer | null,
      activeTexture,
      activeTextureBinding,
      pickingTextureBinding,
      colorMask: [colorMaskValue[0], colorMaskValue[1], colorMaskValue[2], colorMaskValue[3]],
      depthMask: gl.getParameter(gl.DEPTH_WRITEMASK) as boolean,
      blend: gl.isEnabled(gl.BLEND),
      depthTest: gl.isEnabled(gl.DEPTH_TEST),
      dither: gl.isEnabled(gl.DITHER),
      scissorTest: gl.isEnabled(gl.SCISSOR_TEST),
      rasterizerDiscard: gl.isEnabled(gl.RASTERIZER_DISCARD),
    };
  }

  private restorePickingState(gl: WebGL2RenderingContext, state: PickingGlState): void {
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, state.drawFramebuffer);
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, state.readFramebuffer);
    gl.viewport(state.viewport[0], state.viewport[1], state.viewport[2], state.viewport[3]);
    gl.useProgram(state.program);
    gl.bindVertexArray(state.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, state.arrayBuffer);
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, state.pixelPackBuffer);
    gl.bindBuffer(gl.PIXEL_UNPACK_BUFFER, state.pixelUnpackBuffer);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, state.pickingTextureBinding);
    gl.activeTexture(state.activeTexture);
    gl.bindTexture(gl.TEXTURE_2D, state.activeTextureBinding);
    gl.colorMask(...state.colorMask);
    gl.depthMask(state.depthMask);
    this.restoreCapability(gl, gl.BLEND, state.blend);
    this.restoreCapability(gl, gl.DEPTH_TEST, state.depthTest);
    this.restoreCapability(gl, gl.DITHER, state.dither);
    this.restoreCapability(gl, gl.SCISSOR_TEST, state.scissorTest);
    this.restoreCapability(gl, gl.RASTERIZER_DISCARD, state.rasterizerDiscard);
  }

  private restoreCapability(gl: WebGL2RenderingContext, capability: number, enabled: boolean): void {
    if (enabled) {
      gl.enable(capability);
    } else {
      gl.disable(capability);
    }
  }

  private disposePickingResources(): void {
    const gl = this.gl;
    const picking = this.picking;
    if (gl && picking) {
      gl.deleteFramebuffer(picking.framebuffer);
      gl.deleteTexture(picking.resultTexture);
      gl.deleteTexture(picking.cutoffTexture);
      gl.deleteProgram(picking.program);
    }
    this.picking = undefined;
  }

  dispose(): void {
    const gl = this.gl;
    if (gl) {
      this.disposePickingResources();
      if (this.vao) {
        gl.deleteVertexArray(this.vao);
      }
      if (this.responseBuffer) {
        gl.deleteBuffer(this.responseBuffer);
      }
      if (this.iconTexture) {
        gl.deleteTexture(this.iconTexture);
      }
      if (this.program) {
        gl.deleteProgram(this.program);
      }
    }
    this.gl = undefined;
    this.program = undefined;
    this.vao = undefined;
    this.responseBuffer = undefined;
    this.iconTexture = undefined;
    this.uniforms = undefined;
    this.payload = undefined;
  }
}
